import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ExpandableCard from '../components/ExpandableCard';
import SideMenu from '../components/SideMenu';
import { useUnitSettings } from '../hooks/useUnitSettings';
import Cookies from 'js-cookies';
import '../styles/CoupleDashboard.css';

// Map the raw server data into the shape expected by our UI.
function mapServerData(rawData) {
  return rawData.map(item => {
    const timestamp = item['time'];
    const dateObj = new Date(timestamp);
    return {
      date: dateObj,
      forecast_date: dateObj.getTime(),
      precipitation: item['PRECTOTCORR_Average'],
      precipitationMin: item['PRECTOTCORR_Min'],
      precipitationMax: item['PRECTOTCORR_Max'],
      temperature: item['T2M_Average'],
      temperatureMin: item['T2M_Min'],
      temperatureMax: item['T2M_Max'],
      windSpeed: item['WS2M_Average'],
      windSpeedMin: item['WS2M_Min'],
      windSpeedMax: item['WS2M_Max'],
      windDirection: item['WD2M_Average'],
      windDirectionMin: item['WD2M_Min'],
      windDirectionMax: item['WD2M_Max'],
      watts: item['ALLSKY_SFC_SW_DWN_Average'],
      wattsMin: item['ALLSKY_SFC_SW_DWN_Min'],
      wattsMax: item['ALLSKY_SFC_SW_DWN_Max'],
      cloud: item['CLOUD_AMT_Average'],
      cloudMin: item['CLOUD_AMT_Min'],
      cloudMax: item['CLOUD_AMT_Max'],
      rh2m: item['RH2M_Average'],
      rh2mMin: item['RH2M_Min'],
      rh2mMax: item['RH2M_Max'],
    };
  });
}

const CoupleDashboard = ({ user_id }) => {
  const { unitSettings } = useUnitSettings(user_id);
  const navigate = useNavigate();
  const [forecastResults, setForecastResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const removeVenue = async (venue_name, date) => {
    try {
      const res = await fetch(
        'https://api.bestday2marry.com/remove_selected_venue/',
        {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ user_id, venue_name, date })
        }
      );
      if (!res.ok) return;
      const json = await res.json();
      if (json.status === 'success') {
        setForecastResults(curr =>
          curr.filter(
            r =>
              !(
                r.venue.name === venue_name &&
                r.venue.date === date
              )
          )
        );
      }
    } catch (e) {
      // swallow errors
    }
  };

  useEffect(() => {
    async function fetchDashboardForecasts() {
      setLoading(true);
      try {
        const scale = 'hourly';
        const session_cookie = Cookies.getItem('session_cookie') || 'test';
        const url = `https://api.bestday2marry.com/get_couple_dashboard_data/?user_id=${user_id}&scale=${scale}&session_cookie=${session_cookie}&t2m_units=${unitSettings.t2m_units}&precipitation_units=${encodeURIComponent(unitSettings.precipitation_units)}&ws2m_units=${unitSettings.ws2m_units}`;

        const response = await fetch(url, { credentials: 'include' });
        if (!response.ok) {
          setForecastResults([]);
          setLoading(false);
          return;
        }
        const json = await response.json();
        if (json.status === 'success') {
          const results = json.results.map(result => ({
            venue: result.venue,
            matches: mapServerData(result.matches)
          }));
          setForecastResults(results);
        } else {
          setForecastResults([]);
        }
      } catch {
        setForecastResults([]);
      } finally {
        setLoading(false);
      }
    }

    if (user_id) {
      fetchDashboardForecasts();
    } else {
      setLoading(false);
      setError('User ID not provided');
    }
  }, [user_id, unitSettings]);

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="spinner"></div>
        <p>Loading forecast data...</p>
      </div>
    );
  }

  if (error) {
    return <div className="couple-dashboard-error">Error: {error}</div>;
  }

  return (
    <div className="couple-dashboard">
      <SideMenu userId={user_id} />
      <div className="dashboard-content">
        <h1>Your Dashboard</h1>
        <div className="expandable-cards-container">
          {forecastResults.length > 0 ? (
            forecastResults.map(result => (
              <ExpandableCard
                key={`${result.venue.name}-${result.venue.date}`}
                venue={result.venue}
                forecastData={result.matches}
                unitSettings={unitSettings}
                onRemove={removeVenue}
              />
            ))
          ) : (
            <div className="no-venues-placeholder">
              <p>You do not have any venues selected.</p>
              <button
                className="add-venues-button"
                onClick={() => navigate(`/parameters/${user_id}`)}
              >
                Click here to add venues!
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CoupleDashboard;